import { formatDate } from '@angular/common';
import { Component, LOCALE_ID, computed, inject, input, model, output } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { DialogService } from '@vectronic/util';
import { ConfirmationDialogComponent } from '../../modals/confirmation-dialog/confirmation-dialog.component';
import { CardComponent } from '../card/card.component';

export type HistoryItem = {
  id?: string | number;
  author: string;
  timestamp?: string | number | Date;
  type: string;
  text: string;
  actions?: readonly HistoryItemAction[];
};

export type HistoryItemAction = {
  id: string;
  icon: string;
  tooltip: string;
};

export type HistoryType = {
  label: string;
  value: string;
};

@Component({
  selector: 'vectronic-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
  imports: [
    CardComponent,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
  ],
})
export class HistoryComponent {
  private readonly dialogService = inject(DialogService);
  private readonly locale = inject(LOCALE_ID);

  items = input.required<readonly HistoryItem[]>();
  types = input.required<readonly HistoryType[]>();
  selectedType = model('All');
  allType = input('All');
  title = input('History');
  canModifyComments = input.required<boolean>();
  showAddComment = input(true);
  showPopOut = input(false);
  showTypeFilter = input(true);
  displayClose = input(false);
  editableType = input<string>();
  deletableType = input<string>();

  addComment = output<void>();
  editItem = output<HistoryItem>();
  deleteItem = output<HistoryItem>();
  itemAction = output<{ actionId: string; item: HistoryItem }>();
  popOut = output<void>();
  closed = output<void>();

  emptyMessage = computed(() => `No ${this.title().toLowerCase()} exists.`);

  itemsToShow = computed(() => {
    const selectedType = this.selectedType();
    return this.items().filter(
      (item) => selectedType === this.allType() || item.type === selectedType,
    );
  });

  onChangeType(event: MatSelectChange) {
    this.selectedType.set(event.value);
  }

  formatAuthor(author: string): string {
    return author.split('@', 1)[0];
  }

  formatTimestamp(timestamp: string | number | Date): string {
    const date = new Date(timestamp);
    const now = new Date();
    const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const daysAgo = Math.round((today - dateDay) / 86_400_000);
    const time = formatDate(date, 'HH:mm', this.locale);

    if (daysAgo === 0) return `Today at ${time}`;
    if (daysAgo === 1) return `Yesterday at ${time}`;
    return formatDate(date, "d MMM yyyy 'at' HH:mm", this.locale);
  }

  confirmDelete(item: HistoryItem) {
    this.dialogService.open(ConfirmationDialogComponent, {
      data: {
        title: 'Delete Comment',
        message: `Please confirm deletion of comment written by ${item.author} on ${item.timestamp} with the following text: ${item.text}`,
      },
      afterConfirm: () => this.deleteItem.emit(item),
    });
  }
}
